import React, { useEffect, useState } from "react";
import Button, { ButtonType } from "../Assets/Button";
import { X } from "lucide-react";
import {
  getStatesData,
  getTalukasByDistrict,
} from "../../store/slices/kanbanSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import AddTalukaModal from "./AddTalukaModal";

interface AddServicePartnerModalProps {
  isOpen: boolean;
  onClose: () => void;
  name: string;
  mobileNumber: string;
  address: {
    addressLine1: string;
    state: string;
    district: string;
    taluka: string;
    pincode: string;
  };
  companyName: string;
  setName: (v: string) => void;
  setMobileNumber: (v: string) => void;
  setAddress: (v: {
    addressLine1: string;
    state: string;
    district: string;
    taluka: string;
    pincode: string;
  }) => void;
  setCompanyName: (v: string) => void;
  loading: boolean;
  handleAddServicePartner: () => void;
}

const AddServicePartnerModal: React.FC<AddServicePartnerModalProps> = ({
  isOpen,
  onClose,
  name,
  mobileNumber,
  address,
  companyName,
  setName,
  setMobileNumber,
  setAddress,
  setCompanyName,
  loading,
  handleAddServicePartner,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const [showAddTalukaModal, setShowAddTalukaModal] = useState(false);

  const statesData = useSelector((state: RootState) => state.kanban.statesData);
  const talukasData = useSelector(
    (state: RootState) => state.kanban.talukasData,
  );
  const loadingTalukas = useSelector(
    (state: RootState) => state.kanban.loadingTalukas,
  );

  useEffect(() => {
    if (statesData.length === 0) {
      dispatch(getStatesData());
    }
  }, [dispatch, statesData.length]);

  useEffect(() => {
    if (address.district) {
      dispatch(getTalukasByDistrict(address.district));
    }
  }, [dispatch, address.district]);

  // Reset taluka when district changes
  useEffect(() => {
    if (address.district) {
      setAddress({ ...address, taluka: "" });
    }
  }, [address.district]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white w-[420px] max-w-[92%] rounded-2xl shadow-2xl p-6 relative animate-fadeIn">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-gray-800">
            Add Service Partner
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-200 transition"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-gray-600 text-xs font-semibold">
              Full Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full text-xs mt-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 
                           focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <div>
            <label className="text-gray-600 text-xs font-semibold">
              Company Name <span className="text-gray-400">(Optional)</span>
            </label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full text-xs mt-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 
                           focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>

          <div>
            <label className="text-gray-600 text-xs font-semibold">
              Mobile Number
            </label>
            <input
              type="text"
              value={mobileNumber}
              onChange={(e) => setMobileNumber(e.target.value)}
              className="w-full text-xs mt-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 
                           focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>

          <div>
            <span className="text-gray-600 text-xs font-semibold">
              Address Details
            </span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <select
              value={address.state.toLowerCase()}
              onChange={(e) =>
                setAddress({ ...address, state: e.target.value, district: "" })
              }
              className="w-full p-2 text-xs border border-gray-300 rounded-md"
            >
              <option value="">Select State</option>
              {statesData?.map((s) => (
                <option key={s.state} value={s.state.toLowerCase()}>
                  {s.state.capitalizeWords()}
                </option>
              ))}
            </select>

            <select
              value={(address.district || "").toLowerCase()}
              onChange={(e) =>
                setAddress({ ...address, district: e.target.value, taluka: "" })
              }
              disabled={!address.state}
              className="w-full p-2 text-xs border border-gray-300 rounded-md"
            >
              <option value="">Select District</option>
              {statesData
                .find((s) => s.state === address.state.toLowerCase())
                ?.districts?.map((district: string) => (
                  <option key={district} value={district}>
                    {district.capitalizeWords()}
                  </option>
                ))}
            </select>
          </div>

          <div className="flex items-start gap-2 w-full">
            <select
              value={(address.taluka || "").toLowerCase()}
              onChange={(e) =>
                setAddress({ ...address, taluka: e.target.value })
              }
              disabled={!address.district || loadingTalukas}
              className="flex-1 p-2 text-xs border border-gray-300 rounded-md"
            >
              <option value="">
                {loadingTalukas
                  ? "Loading talukas..."
                  : talukasData?.taluka?.length
                    ? "Select Taluka"
                    : "No taluka available"}
              </option>

              {talukasData?.taluka?.map((taluka: string) => (
                <option key={taluka} value={taluka}>
                  {taluka.capitalizeWords()}
                </option>
              ))}
            </select>

            {address.district && !loadingTalukas && (
              <Button
                type={ButtonType.Extra}
                onClick={() => setShowAddTalukaModal(true)}
                className="text-xs whitespace-nowrap"
              >
                Add Taluka
              </Button>
            )}
          </div>

          <div className="flex items-center justify-between gap-2">
            <input
              value={address.addressLine1}
              onChange={(e) =>
                setAddress({ ...address, addressLine1: e.target.value })
              }
              className="w-full text-xs mt-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 
                           focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="Address Line 1"
            />

            <input
              type="text"
              value={address.pincode}
              onChange={(e) =>
                setAddress({ ...address, pincode: e.target.value })
              }
              className="w-full text-xs mt-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 
                           focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="Pincode"
            />
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <Button
              type={ButtonType.Cancel}
              onClick={onClose}
              className="text-xs"
            >
              Cancel
            </Button>
            <Button
              type={ButtonType.Submit}
              className="text-xs"
              onClick={handleAddServicePartner}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Partner"}
            </Button>
          </div>
        </div>
      </div>
      <AddTalukaModal
        isOpen={showAddTalukaModal}
        onClose={() => setShowAddTalukaModal(false)}
        district={address.district}
      />
    </div>
  );
};

export default AddServicePartnerModal;
